import React, { Component, useEffect, useState } from "react";
import { useSelector } from "react-redux";
import styles from "./sidebar.module.css";
import { get } from "util/requests";
import { arrayHaveCommonItems } from "util/helpers";
import CheckBoxIcon from "@mui/icons-material/TaskAlt";
import MenuItem from "@mui/material/MenuItem";
import Select from "@mui/material/Select";
import PanoramaFishEyeIcon from "@mui/icons-material/PanoramaFishEye";
import SmsOutlined from "@mui/icons-material/Sms";
import router from "routes";
import DescriptionIcon from "@mui/icons-material/Description";
import { ArrowBackIcon } from "ui/icons";
import { CheckIcon, DataIcon, DataIconBlue, RespondedQueryIcon, OpenQueryIcon, ResolvedQueryIcon } from "ui/icons";
import Tooltip from "@mui/material/Tooltip";
import strings from "strings";

const layer1 = [styles.sidebar_layer, styles.sidebar_layer1].join(" ");
const layer2 = [styles.sidebar_layer, styles.sidebar_layer2].join(" ");
const layer2_active = [styles.sidebar_layer, styles.sidebar_layer2, styles.layer2_active].join(" ");
const layer3 = [styles.sidebar_layer, styles.sidebar_layer3].join(" ");

const renderRowIcons = (permissions, { query_status, status, count = 0, total_count = 0, flags }) => {
  let ret = [];

  if (arrayHaveCommonItems(permissions, ["view_query_status", "start_query", "respond_query", "resolve_query"])) {
    if (query_status === "open" || query_status === "okay") {
      ret.push(
        <div className={styles.query_icon_wrapper}>
          <Tooltip title={strings.page_query_open}>
            <OpenQueryIcon fontSize="14px" lineHeight="14px" />
          </Tooltip>
        </div>
      );
    } else if (query_status === "resolved" || query_status === "closed") {
      ret.push(
        <div className={styles.query_icon_wrapper}>
          <Tooltip title={strings.page_query_closed}>
            <ResolvedQueryIcon fontSize="14px" lineHeight="14px" />
          </Tooltip>
        </div>
      );
    } else if (query_status === "responded" || query_status === "action") {
      ret.push(
        <div className={styles.query_icon_wrapper}>
          <Tooltip title={strings.page_query_responded}>
            <RespondedQueryIcon fontSize="14px" lineHeight="14px" />
          </Tooltip>
        </div>
      );
    }
  }

  if (permissions.includes("set_pi_signed_flag") || permissions.includes("view_pi_signed_flag")) {
    if (flags.includes("resite_signoff")) {
      ret.push(
        <div className={styles.re_icon}>
          <Tooltip title={strings.re_site_signoff}>S</Tooltip>
        </div>
      );
    } else if (flags.includes("all_site_signoff")) {
      ret.push(
        <div className={styles.all_icon}>
          <Tooltip title={strings.crf_site_signoff}>S</Tooltip>
        </div>
      );
    }
  }

  if (permissions.includes("set_monitored_flag") || permissions.includes("view_monitored_flag")) {
    if (flags.includes("remonitored")) {
      ret.push(
        <div className={styles.re_icon}>
          <Tooltip title={strings.re_monitor}>M</Tooltip>
        </div>
      );
    } else if (flags.includes("all_monitored")) {
      ret.push(
        <div className={styles.all_icon}>
          <Tooltip title={strings.all_fields_monitored}>M</Tooltip>
        </div>
      );
    } else if (flags.includes("monitored")) {
      ret.push(
        <div className={styles.partial_icon}>
          <Tooltip title={strings.partial_monitored_flag}>M</Tooltip>
        </div>
      );
    }
  }

  if (permissions.includes("set_qc_flag") || permissions.includes("view_qc_flag")) {
    if (flags.includes("reqa")) {
      ret.push(
        <div className={styles.re_icon}>
          <Tooltip title={strings.re_qa}>Q</Tooltip>
        </div>
      );
    } else if (flags.includes("all_qa")) {
      ret.push(
        <div className={styles.all_icon}>
          <Tooltip title={strings.all_qa_flag}>Q</Tooltip>
        </div>
      );
    } else if (flags.includes("qa")) {
      ret.push(
        <div className={styles.partial_icon}>
          <Tooltip title={strings.partial_qa_flag}>Q</Tooltip>
        </div>
      );
    }
  }

  if (flags?.includes("all_data")) {
    ret.push(
      <Tooltip title={strings.all_data_flag}>
        <DataIconBlue fontSize={"16px"} color="blue" />
      </Tooltip>
    );
  } else if (flags?.includes("data")) {
    ret.push(
      <Tooltip title={strings.partial_data_flag}>
        <DataIcon fontSize={"16px"} />
      </Tooltip>
    );
  }
  if (status === "data_complete") {
    ret.push(
      <Tooltip title={strings.data_complete_flag}>
        <CheckIcon fontSize="16px" />
      </Tooltip>
    );
  }

  if (total_count) {
    ret.push(<div className={styles.count_icon}>{total_count}</div>);
  }
  return ret;
};

const Row = function (props) {
  return (
    <div key={props.crf_id} {...props} className={props.is_active ? layer2_active : layer2}>
      <div className={styles.sidebar_text}>{props.name}</div>
      <div className={styles.icon_tags}>{renderRowIcons(props.permissions, props)}</div>
    </div>
  );
};

export default function Sidebar(props) {
  const { crf_id, visit_index, subject_id, crfList, globalFormsList, hideSidebar, corelabCrfList } = props;

  const [visits, setVisits] = useState([]);
  const study_id = useSelector((state) => state.study.active_study_id);
  const permissions = useSelector((state) => state.user.userData.permissions);

  useEffect(() => {
    get(`/crf_visits/${study_id}/${subject_id}`).then((ret) => {
      let visits = ret.data.visits;
      setVisits(visits);
    });
  }, []);

  return (
    <div className={styles.sidebar}>
      <div className={styles.close_icon}>
        <ArrowBackIcon onClick={() => hideSidebar(true)} fontSize={"20px"} />
      </div>
      <div>
        <div className={styles.visit_select_header}>
          <strong>Current Visit</strong>
        </div>
        <div className={styles.visit_select_wrapper}>
          <Select
            labelId="demo-select-small"
            value={visit_index}
            sx={{
              "& .Mui-focusVisible": { fontSize: "10px" },
              fontSize: 12,
              width: "90%",
              height: 30,
              padding: 0,
              alignSelf: "center",
            }}
            MenuProps={{
              PaperProps: {
                sx: {
                  "& .MuiMenuItem-root": {
                    padding: "6px",
                    fontSize: "14px",
                  },
                },
              },
            }}
          >
            {visits?.map((visit) => (
              <MenuItem
                key={visit.id}
                value={visit.id}
                onClick={() => {
                  router.navigate(`/subjects/${subject_id}/edc/visit/${visit.id}`);
                }}
              >
                {visit.name}
              </MenuItem>
            ))}
          </Select>
        </div>
      </div>
      <div className={layer1}>
        <div>
          <strong>Visit Pages</strong>
        </div>
      </div>

      {crfList &&
        crfList
          .filter(({ category }) => category === "main")
          .map((crf) => (
            <Row
              key={crf.id}
              id={crf.id}
              name={crf.name}
              is_active={crf.id === crf_id || undefined}
              query_status={crf.query_status}
              status={crf.status}
              flags={crf?.flags || []}
              onClick={() => router.navigate(`/subjects/${subject_id}/edc/visit/${visit_index}/crf/${crf.id}`)}
              permissions={permissions}
            />
          ))}
      {crfList && crfList.filter(({ category }) => category === "study_level_single_entry").length ? (
        <React.Fragment>
          <div style={{ height: 14 }} />
          <div className={layer1}>
            <div>
              <strong>Study Level Single Entry Forms</strong>
            </div>
          </div>
          {crfList &&
            crfList
              .filter(({ category }) => category === "study_level_single_entry")
              .map((crf) => (
                <Row
                  key={crf.id}
                  id={crf.id}
                  name={crf.name}
                  is_active={crf.id === crf_id || undefined}
                  query_status={crf.query_status}
                  status={crf.status}
                  flags={crf?.flags || []}
                  onClick={() => router.navigate(`/subjects/${subject_id}/edc/visit/${visit_index}/crf/${crf.id}`)}
                  permissions={permissions}
                />
              ))}
        </React.Fragment>
      ) : null}
      {globalFormsList && globalFormsList.length ? (
        <React.Fragment>
          <div style={{ height: 14 }} />
          <div className={layer1}>
            <div>
              <strong>Study Level Multiple Entry Forms</strong>
            </div>
          </div>
          {globalFormsList &&
            globalFormsList
              .filter((f) => !f.hidden)
              .map((form) => (
                <Row
                  key={form.id}
                  id={form.id}
                  name={form.name}
                  is_active={form.id === crf_id || undefined}
                  status={"status"}
                  flags={form?.flags || []}
                  count={form.visit_count || 0}
                  total_count={form.total || 0}
                  onClick={() =>
                    router.navigate(`/subjects/${subject_id}/edc/visit/${visit_index}/multi_entry/${form.id}`)
                  }
                  permissions={permissions}
                />
              ))}
        </React.Fragment>
      ) : null}
      {corelabCrfList && corelabCrfList.length ? (
        <React.Fragment>
          <div style={{ height: 14 }} />
          <div className={layer1}>
            <div>
              <strong>Corelab Imaging Forms</strong>
            </div>
          </div>
          {corelabCrfList &&
            corelabCrfList
              .filter((f) => !f.hidden)
              .map((form) => (
                <Row
                  key={form.id}
                  id={form.id}
                  name={form.name}
                  is_active={form.id === crf_id || undefined}
                  status={"status"}
                  count={form.visit_count || 0}
                  total_count={form.total || 0}
                  flags={form?.flags || []}
                  onClick={() => router.navigate(`/subjects/${subject_id}/edc/visit/${visit_index}/corelab/${form.id}`)}
                  permissions={permissions}
                />
              ))}
        </React.Fragment>
      ) : null}
      <div style={{ height: 20 }} />
    </div>
  );
}
